// @ts-nocheck
import create from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { axios } from "./axios";
import { toast } from "sonner";
import { showError } from "../actions/globalActions";

const baseUrl = `${process.env.API_URL}/api/v1`;

const createAuthHeader = (token) => ({
  headers: {
    'Authorization': `Bearer ${token}`,
  },
});

// Homepage posts (announcements). Mirrors layoutStore's conventions:
// mutating actions are higher-order (component binds `getToken` at selector
// time), reads unwrap the API's { error, result, status } envelope.
const usePostStore = create(immer((set) => ({
  posts: [],

  // Admin list — drafts included (auth required).
  fetchPosts: (getToken) => {
    return async (leagueName = "") => {
      try {
        const token = await getToken();
        const { data } = await axios.get(
          `${baseUrl}/posts/d/organizationName/${leagueName}`,
          createAuthHeader(token)
        );
        set((state) => {
          state.posts = data.result || [];
        });
      } catch (error) {
        console.error("Error in fetchPosts:", error);
      }
    };
  },

  addPost: (getToken) => {
    return async (post = {}, cb = () => {}) => {
      try {
        const token = await getToken();
        const { data } = await axios.post(`${baseUrl}/posts/d`, post, createAuthHeader(token));
        data?.error ? toast.error(data?.error) : toast.success(`Post created`);
        cb && cb(data?.result);
        set((state) => {
          if (data?.result) state.posts.unshift(data.result);
        });
      } catch (error) {
        showError("Error", "Could not create post");
        console.error("Error in addPost:", error);
      }
    };
  },

  updatePost: (getToken) => {
    return async (post = {}, cb = () => {}) => {
      try {
        const token = await getToken();
        const { data } = await axios.put(`${baseUrl}/posts/d/${post._id}`, post, createAuthHeader(token));
        data?.error ? toast.error(data?.error) : toast.success(`Post saved`);
        cb && cb(data?.result);
        set((state) => {
          const index = state.posts.findIndex((p) => p._id === post._id);
          if (index !== -1 && data?.result) {
            state.posts[index] = data.result;
          }
          // Pinned post (INT-0048): the server unpinned the siblings; mirror
          // that locally so the canvas and sidebar update without a reload.
          if (data?.result?.pinned) {
            state.posts.forEach((p) => { if (p._id !== post._id && p.pinned) p.pinned = undefined; });
          }
        });
      } catch (error) {
        showError("Error", "Could not save post");
        console.error("Error in updatePost:", error);
      }
    };
  },

  // updatePost minus the toasts — the canvas editor autosaves every ~1.5s,
  // and a "Post saved" toast per keystroke-burst is noise. The autosave chip
  // in the editor's top bar is the feedback channel; failures REJECT so the
  // caller can requeue the dirty draft (unlike the swallow-and-toast actions
  // above, silence here would mean silent data loss).
  savePostSilent: (getToken) => {
    return async (post = {}) => {
      const token = await getToken();
      const { data } = await axios.put(`${baseUrl}/posts/d/${post._id}`, post, createAuthHeader(token));
      if (data?.error) throw new Error(data.error);
      set((state) => {
        const index = state.posts.findIndex((p) => p._id === post._id);
        if (index !== -1 && data?.result) {
          state.posts[index] = data.result;
        }
        if (data?.result?.pinned) {
          state.posts.forEach((p) => { if (p._id !== post._id && p.pinned) p.pinned = undefined; });
        }
      });
      return data?.result;
    };
  },

  deletePost: (getToken) => {
    return async (post = {}, cb = () => {}) => {
      try {
        const token = await getToken();
        const { data } = await axios.delete(`${baseUrl}/posts/d/${post._id}`, createAuthHeader(token));
        data?.error ? toast.error(data?.error) : toast.success(`Post deleted`);
        cb && cb(data?.result);
        set((state) => {
          state.posts = state.posts.filter((p) => p._id !== post._id);
        });
      } catch (error) {
        showError("Error", "Could not delete post");
        console.error("Error in deletePost:", error);
      }
    };
  },
})));

export default usePostStore;
